import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Pencil, Trash2, Trophy, XCircle, Building2, User, Clock, AlertTriangle } from "lucide-react"
import { useDeal, useDeleteDeal, useChangeDealPhase, useWinDeal, useLoseDeal } from "@/hooks/use-deals"
import { RecordNavigation } from "@/components/record-navigation"
import { useDealType } from "@/hooks/use-deal-types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { ActivityTimeline } from "@/components/activity-timeline"
import { NotesTab } from "@/components/notes-tab"
import { useEntityRelated } from "@/hooks/use-entity-related"
import { TasksList, DocumentsList, QuotesList, InvoicesList } from "@/components/related-entities-list"
import { formatDate, formatCurrency } from "@/lib/utils"
import { toast } from "sonner"
import type { DealPhase } from "@/lib/types"

const FIELD_LABELS: Record<string, string> = {
  value: "Wartosc",
  company_id: "Kontrahent",
  owner_id: "Opiekun",
  expected_close_date: "Planowane zamkniecie",
  description: "Opis",
  probability: "Prawdopodobienstwo",
  contacts: "Kontakty (min. 1)",
  products: "Produkty (min. 1)",
}

export function DealDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useDeal(id)
  const deleteMutation = useDeleteDeal()
  const changePhaseMutation = useChangeDealPhase()
  const winMutation = useWinDeal()
  const loseMutation = useLoseDeal()

  const [gateDialog, setGateDialog] = useState<{ open: boolean; missingFields: string[]; phaseName: string }>({
    open: false,
    missingFields: [],
    phaseName: "",
  })

  const deal = data?.data
  const { data: dealTypeData } = useDealType(deal?.deal_type?.id)
  const phases = dealTypeData?.data?.phases ?? []
  const { data: relatedData } = useEntityRelated("Deal", id)
  const related = relatedData?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac te szanse sprzedazy?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Szansa usunieta")
    navigate("/deals")
  }

  const handlePhaseClick = async (phase: DealPhase) => {
    if (!id || !deal || deal.status !== "open") return
    if (phase.id === deal.phase.id) return

    try {
      await changePhaseMutation.mutateAsync({ id, phase_id: phase.id })
      if (phase.is_won) toast.success("Szansa wygrana!")
      else if (phase.is_lost) toast.success("Szansa przegrana")
      else toast.success(`Etap zmieniony na: ${phase.name}`)
    } catch (err: unknown) {
      const error = err as { missing_fields?: string[] }
      if (error.missing_fields && error.missing_fields.length > 0) {
        setGateDialog({
          open: true,
          missingFields: error.missing_fields,
          phaseName: phase.name,
        })
      } else {
        toast.error("Nie udalo sie zmienic etapu")
      }
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!deal) {
    return <div className="text-muted-foreground">Nie znaleziono szansy sprzedazy</div>
  }

  const statusClasses: Record<string, string> = { open: "border-primary text-primary", won: "border-green-500 text-green-500", lost: "border-red-500 text-red-500" }
  const statusLabels: Record<string, string> = { open: "Otwarta", won: "Wygrana", lost: "Przegrana" }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Pipeline", href: "/deals" },
          { label: deal.name },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/deals")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <RecordNavigation entityType="deals" currentId={id} basePath="/deals" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="page-title">{deal.name}</h1>
              <Badge variant="outline" className={`text-xs ${statusClasses[deal.status]}`}>
                {statusLabels[deal.status]}
              </Badge>
            </div>
            {deal.company && (
              <p className="text-sm text-muted-foreground">
                <Building2 size={12} className="inline mr-1" />{deal.company.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {deal.status === "open" && (
            <>
              <Button variant="outline" size="sm" onClick={() => winMutation.mutateAsync(id!).then(() => toast.success("Szansa wygrana!"))}>
                <Trophy size={14} /> Wygrana
              </Button>
              <Button variant="outline" size="sm" onClick={() => loseMutation.mutateAsync({ id: id! }).then(() => toast.success("Szansa przegrana"))}>
                <XCircle size={14} /> Przegrana
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/deals/${id}/edit`)}>
            <Pencil size={14} /> {t("common.edit")}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Phase Stepper */}
      {phases.length > 0 && (
        <div className="flex gap-1">
          {phases.filter(p => !p.is_won && !p.is_lost).map((phase) => {
            const currentIdx = phases.findIndex(p => p.id === deal.phase.id)
            const phaseIdx = phases.findIndex(p => p.id === phase.id)
            const isActive = phase.id === deal.phase.id
            const isPassed = phaseIdx < currentIdx

            return (
              <button
                key={phase.id}
                onClick={() => handlePhaseClick(phase)}
                disabled={deal.status !== "open"}
                className={`flex-1 rounded-md py-2 px-3 text-center text-xs font-medium transition-all disabled:cursor-not-allowed ${
                  !isActive && !isPassed ? "bg-muted text-muted-foreground" : ""
                }`}
                style={isActive ? {
                  backgroundColor: phase.color,
                  color: "#fff",
                  borderLeft: `3px solid ${phase.color}`,
                } : isPassed ? {
                  backgroundColor: `${phase.color}33`,
                  color: phase.color,
                } : undefined}
              >
                {phase.name}
                <span className="block text-[10px] opacity-70">{phase.probability}%</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Przeglad</TabsTrigger>
              <TabsTrigger value="notes">Notatki{(related?.notes?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.notes?.count}</span>}</TabsTrigger>
              <TabsTrigger value="products">Produkty ({deal.products.length})</TabsTrigger>
              <TabsTrigger value="contacts">Kontakty ({deal.contacts.length})</TabsTrigger>
              <TabsTrigger value="activities">Aktywnosci</TabsTrigger>
              <TabsTrigger value="tasks">Zadania{(related?.tasks?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.tasks?.count}</span>}</TabsTrigger>
              <TabsTrigger value="documents">Dokumenty{(related?.documents?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.documents?.count}</span>}</TabsTrigger>
              <TabsTrigger value="quotes">Oferty{(related?.quotes?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.quotes?.count}</span>}</TabsTrigger>
              <TabsTrigger value="invoices">Faktury{(related?.invoices?.count ?? 0) > 0 && <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold bg-muted text-muted-foreground">{related?.invoices?.count}</span>}</TabsTrigger>
              <TabsTrigger value="history">Historia</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* Value card */}
              <div className="surface-card p-4 space-y-3">
                <h2 className="text-sm font-semibold text-foreground">Wartosc</h2>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Wartosc</span>
                    <p className="text-lg font-mono font-semibold text-foreground">
                      {deal.value != null ? formatCurrency(deal.value, deal.currency) : "\u2014"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Prawdopodobienstwo</span>
                    <p className="text-lg font-semibold text-foreground">
                      {deal.probability != null ? `${deal.probability}%` : "\u2014"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Wazona wartosc</span>
                    <p className="text-lg font-mono font-semibold text-primary">
                      {deal.weighted_value != null ? formatCurrency(deal.weighted_value, deal.currency) : "\u2014"}
                    </p>
                  </div>
                </div>
              </div>

              {deal.description && (
                <div className="surface-card p-4 space-y-2">
                  <h2 className="text-sm font-semibold text-foreground">Opis</h2>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{deal.description}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="products" className="mt-4">
              <div className="surface-card">
                {deal.products.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak produktow</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Produkt</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Ilosc</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Cena netto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Rabat</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Suma netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deal.products.map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="px-4 py-2 text-foreground">{p.name}</td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">{p.quantity} {p.unit}</td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">{p.unit_price_net != null ? formatCurrency(p.unit_price_net) : "\u2014"}</td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">{p.discount_percent > 0 ? `${p.discount_percent}%` : "\u2014"}</td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-foreground">{formatCurrency(p.line_total_net)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t font-semibold">
                        <td colSpan={4} className="px-4 py-2 text-right text-foreground">Suma netto:</td>
                        <td className="px-4 py-2 text-right font-mono text-primary">{formatCurrency(deal.total_net)}</td>
                      </tr>
                      <tr className="font-semibold">
                        <td colSpan={4} className="px-4 py-2 text-right text-foreground">Suma brutto:</td>
                        <td className="px-4 py-2 text-right font-mono text-primary">{formatCurrency(deal.total_gross)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="mt-4">
              <div className="surface-card">
                {deal.contacts.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak kontaktow</p>
                ) : (
                  <div className="divide-y divide-border">
                    {deal.contacts.map((c) => (
                      <div key={c.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold bg-primary text-primary-foreground">
                          {c.first_name[0]}{c.last_name[0]}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {c.first_name} {c.last_name}
                            {c.is_primary && <Badge variant="secondary" className="ml-2 text-xs">Glowny</Badge>}
                          </p>
                          {c.role && <p className="text-xs text-muted-foreground">{c.role}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <NotesTab entityType="Deal" entityId={deal.id} />
            </TabsContent>

            <TabsContent value="activities" className="mt-4">
              <ActivityTimeline entityType="Deal" entityId={deal.id} />
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <div className="surface-card">
                <TasksList items={related?.tasks?.items ?? []} />
              </div>
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <div className="surface-card">
                <DocumentsList items={related?.documents?.items ?? []} />
              </div>
            </TabsContent>

            <TabsContent value="quotes" className="mt-4">
              <div className="surface-card">
                <QuotesList items={related?.quotes?.items ?? []} />
              </div>
            </TabsContent>

            <TabsContent value="invoices" className="mt-4">
              <div className="surface-card">
                <InvoicesList items={related?.invoices?.items ?? []} />
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <div className="space-y-3">
                {deal.phase_history.length === 0 ? (
                  <p className="text-center text-sm py-8 text-muted-foreground">Brak historii</p>
                ) : (
                  deal.phase_history.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 surface-card p-3">
                      <Clock size={16} className="mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm text-foreground">
                          {entry.from_phase
                            ? <>{entry.from_phase.name} &rarr; <strong>{entry.to_phase.name}</strong></>
                            : <>Utworzono w etapie: <strong>{entry.to_phase.name}</strong></>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {entry.user && `${entry.user.first_name} ${entry.user.last_name} \u00B7 `}
                          {formatDate(entry.created_at)}
                          {entry.duration_seconds != null && entry.duration_seconds > 0 && (
                            <> &middot; {Math.round(entry.duration_seconds / 86400)} dni w poprzednim etapie</>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Pipeline</span>
              <p className="text-sm text-foreground">{deal.deal_type.name}</p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Etap</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: deal.phase.color }} />
                <p className="text-sm text-foreground">{deal.phase.name}</p>
              </div>
            </div>
            {deal.owner && (
              <div>
                <span className="text-xs text-muted-foreground">{t("deals.owner")}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{deal.owner.first_name} {deal.owner.last_name}</p>
                </div>
              </div>
            )}
            <div>
              <span className="text-xs text-muted-foreground">Dni w etapie</span>
              <p className="text-sm text-foreground">{deal.days_in_phase}</p>
            </div>
            {deal.expected_close_date && (
              <div>
                <span className="text-xs text-muted-foreground">{t("deals.expectedClose")}</span>
                <p className="text-sm text-foreground">{formatDate(deal.expected_close_date)}</p>
              </div>
            )}
            {deal.actual_close_date && (
              <div>
                <span className="text-xs text-muted-foreground">Data zamkniecia</span>
                <p className="text-sm text-foreground">{formatDate(deal.actual_close_date)}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(deal.created_at)}</p>
            </div>
            {deal.tags.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Tagi</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {deal.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Phase Gate Dialog */}
      <Dialog open={gateDialog.open} onOpenChange={(open) => setGateDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-warning" />
              Wymagane pola
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Aby przejsc do etapu <strong>{gateDialog.phaseName}</strong>, uzupelnij brakujace pola:
            </p>
            <ul className="space-y-1.5">
              {gateDialog.missingFields.map((field) => (
                <li key={field} className="flex items-center gap-2 text-sm text-foreground">
                  <div className="h-1.5 w-1.5 rounded-full bg-error" />
                  {FIELD_LABELS[field] || field}
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGateDialog(prev => ({ ...prev, open: false }))}>
              Zamknij
            </Button>
            <Button onClick={() => {
              setGateDialog(prev => ({ ...prev, open: false }))
              navigate(`/deals/${id}/edit`)
            }}>
              Edytuj szanse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
